import { useCallback, useEffect, useRef, useState } from 'react';

import { JobFormData } from '@/jobs/application/types/JobTypes';
import { LoggerService } from '@/common/infrastructure/services/LoggerService';
import { TimeZoneService } from '@/jobs/application/services/TimeZoneService';
import { TypeaheadOption } from '@/common/presentation/components/inputs/Typeahead';
import { organizationTimezoneService } from '@/common/infrastructure/services/OrganizationTimezoneService';
import { useFormikContext } from 'formik';
import { useTimeZoneTypeaheadFetch } from '@/jobs/application/adapters/listTimeZonesAutocomplete';

// Create a logger instance for this hook
const logger = LoggerService.create('useJobSiteTimeZone');

interface JobSiteData {
  id: number;
  jobSiteName: string;
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

interface UseJobSiteTimeZoneProps {
  selectedJobSite: JobSiteData | null;
  availableTimezones?: TypeaheadOption<string>[]; // Available timezone options for matching
  onTimeZonePopulated?: (timeZone: TypeaheadOption<string>) => void;
  onError?: (error: string) => void;
  onJobSiteSelected?: () => void; // New callback to notify when jobsite is selected
  isNewJob?: boolean; // Whether this is a new job (affects fallback behavior)
}

interface UseJobSiteTimeZoneResult {
  isPopulatingTimeZone: boolean;
  timeZoneError: string | null;
  clearTimeZoneError: () => void;
}

export const useJobSiteTimeZone = ({
  selectedJobSite,
  availableTimezones = [],
  onTimeZonePopulated,
  onError,
  onJobSiteSelected,
  isNewJob = false
}: UseJobSiteTimeZoneProps): UseJobSiteTimeZoneResult => {
  const { setFieldValue, values } = useFormikContext<JobFormData>();
  const [isPopulatingTimeZone, setIsPopulatingTimeZone] = useState(false);
  const [timeZoneError, setTimeZoneError] = useState<string | null>(null);
  const timeZoneService = useRef<TimeZoneService | null>(null);
  const lastJobSiteId = useRef<number | null>(null);
  const { fetchTimeZoneById, initialTimeZones, isLoading: timezonesLoading } = useTimeZoneTypeaheadFetch();

  // Use provided availableTimezones or fall back to initialTimeZones from hook
  const timezonesToUse = availableTimezones.length > 0 ? availableTimezones : initialTimeZones;

  // Check if we have timezones available for matching
  const hasTimezonesAvailable = timezonesToUse.length > 0;

  // Initialize the TimeZoneService
  useEffect(() => {
    if (!timeZoneService.current) {
      timeZoneService.current = TimeZoneService.getInstance();
      logger.info('TimeZoneService initialized');
    }
  }, []);

  // Clear error when job site changes
  useEffect(() => {
    if (selectedJobSite?.id !== lastJobSiteId.current) {
      setTimeZoneError(null);
      // Don't update lastJobSiteId here - let the main effect handle it
    }
  }, [selectedJobSite?.id]);

  const clearTimeZoneError = useCallback(() => {
    setTimeZoneError(null);
  }, []);

  // Function to populate timezone based on job site
  const populateTimeZoneFromJobSite = useCallback(
    async (jobSite: JobSiteData): Promise<void> => {
      logger.info('populateTimeZoneFromJobSite called', {
        jobSiteId: jobSite.id,
        jobSiteName: jobSite.jobSiteName,
        timeZoneServiceInitialized: timeZoneService.current?.isInitialized() || false,
        currentTimeZone: values.timeZone
      });

      if (!timeZoneService.current || !timeZoneService.current.isInitialized()) {
        logger.warn('TimeZoneService not initialized, skipping timezone population');
        return;
      }

      // Store the current timezone value to preserve it during async operation
      // This prevents the field from being cleared while we fetch the new timezone
      const currentTimezoneValue = values.timeZone;

      // Always populate timezone when job site changes, even if user has already selected one
      // This allows users to override their previous selection with the job site's timezone
      logger.debug('Populating timezone for job site (overriding any existing selection)');

      logger.info('Starting timezone population process', {
        jobSiteId: jobSite.id,
        jobSiteName: jobSite.jobSiteName
      });

      setIsPopulatingTimeZone(true);
      setTimeZoneError(null);

      try {
        logger.debug('Populating timezone for job site', {
          jobSiteId: jobSite.id,
          jobSiteName: jobSite.jobSiteName,
          latitude: jobSite.latitude,
          longitude: jobSite.longitude,
          address: jobSite.address,
          availableTimezonesCount: timezonesToUse.length
        });

        // Use TimeZoneService.getTimezoneOptionForJobSite which handles the full flow:
        // 1. Gets timezone from Google Maps API (coordinates or address)
        // 2. Calculates UTC offset
        // 3. Matches to backend timezone options
        const timeZoneOption = await timeZoneService.current.getTimezoneOptionForJobSite(
          jobSite.latitude ?? undefined,
          jobSite.longitude ?? undefined,
          timezonesToUse,
          jobSite.address ?? undefined
        );

        if (!timeZoneOption) {
          // For new jobs, don't use localStorage fallback here - let useJobTimezoneHierarchy
          // handle the complete hierarchy (Jobsite coordinates > Organization default > User override > Browser timezone)
          if (isNewJob) {
            logger.info('No timezone found for jobsite (new job), leaving field empty for hierarchy to apply', {
              jobSiteId: jobSite.id
            });
            // Clear the field to ensure it's empty - useJobTimezoneHierarchy will apply full hierarchy
            setFieldValue('timeZone', null);
            return;
          }

          // For existing jobs, try to use localStorage timezone as fallback
          const storedTimezone = organizationTimezoneService.getOrganizationDefaultTimezone();
          if (storedTimezone) {
            try {
              logger.info('No timezone found for jobsite, using localStorage timezone preference', {
                storedTimezone,
                jobSiteId: jobSite.id
              });
              const storedTimezoneOption = await fetchTimeZoneById(storedTimezone);
              if (storedTimezoneOption) {
                setFieldValue('timeZone', storedTimezoneOption.value);
                onTimeZonePopulated?.(storedTimezoneOption);
                logger.info('Successfully used localStorage timezone preference', {
                  timeZoneId: storedTimezoneOption.value,
                  timeZoneName: storedTimezoneOption.label
                });
                return;
              }
            } catch (error) {
              logger.warn('Failed to fetch localStorage timezone, will show error', error);
            }
          }

          // Only show error if localStorage also has no timezone
          const errorMessage = 'Unable to determine timezone for this job site. Please select a timezone manually.';
          setTimeZoneError(errorMessage);
          onError?.(errorMessage);
          // Preserve existing timezone if we can't determine a new one
          if (currentTimezoneValue) {
            setFieldValue('timeZone', currentTimezoneValue);
          }
          return;
        }

        // Set the timezone in the form
        setFieldValue('timeZone', timeZoneOption.value);
        logger.info('Successfully populated timezone from job site', {
          jobSiteId: jobSite.id,
          timeZoneId: timeZoneOption.value,
          timeZoneName: timeZoneOption.label
        });

        // Call the callback if provided
        onTimeZonePopulated?.(timeZoneOption);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to populate timezone';
        logger.error('Error populating timezone from job site', {
          jobSiteId: jobSite.id,
          error: errorMessage
        });

        // For new jobs, don't use localStorage fallback here - let useJobTimezoneHierarchy
        // handle the complete hierarchy (Jobsite coordinates > Organization default > User override > Browser timezone)
        if (isNewJob) {
          logger.info('Timezone population failed (new job), leaving field empty for hierarchy to apply', {
            jobSiteId: jobSite.id,
            error: errorMessage
          });
          // Clear the field to ensure it's empty - useJobTimezoneHierarchy will apply full hierarchy
          setFieldValue('timeZone', null);
          return;
        }

        // For existing jobs, try to use localStorage timezone as fallback
        const storedTimezone = organizationTimezoneService.getOrganizationDefaultTimezone();
        if (storedTimezone) {
          try {
            logger.info('Population failed, using localStorage timezone preference as fallback', {
              storedTimezone,
              error: errorMessage
            });
            const storedTimezoneOption = await fetchTimeZoneById(storedTimezone);
            if (storedTimezoneOption) {
              setFieldValue('timeZone', storedTimezoneOption.value);
              onTimeZonePopulated?.(storedTimezoneOption);
              logger.info('Successfully used localStorage timezone preference', {
                timeZoneId: storedTimezoneOption.value,
                timeZoneName: storedTimezoneOption.label
              });
              // Clear error since we successfully used localStorage fallback
              setTimeZoneError(null);
              return;
            }
          } catch (fallbackError) {
            logger.warn('Failed to fetch localStorage timezone, will show original error', fallbackError);
          }
        }

        // Only show error if localStorage also has no timezone
        setTimeZoneError(errorMessage);
        onError?.(errorMessage);
        // Preserve existing timezone if population fails
        if (currentTimezoneValue) {
          setFieldValue('timeZone', currentTimezoneValue);
        }
      } finally {
        setIsPopulatingTimeZone(false);
      }
    },
    [timeZoneService, values.timeZone, setFieldValue, timezonesToUse, onTimeZonePopulated, onError, fetchTimeZoneById]
  );

  // Effect to automatically populate timezone when job site is selected
  useEffect(() => {
    if (selectedJobSite && selectedJobSite.id !== lastJobSiteId.current) {
      logger.debug('Job site changed, should populate timezone', {
        newJobSiteId: selectedJobSite.id,
        lastJobSiteId: lastJobSiteId.current,
        jobSiteName: selectedJobSite.jobSiteName
      });

      logger.info('Job site changed, checking if we should populate timezone', {
        newJobSiteId: selectedJobSite.id,
        lastJobSiteId: lastJobSiteId.current,
        jobSiteName: selectedJobSite.jobSiteName
      });

      // Only populate if we have either coordinates or address
      // Check for truthy values and non-empty strings
      const hasCoordinates = selectedJobSite.latitude != null && selectedJobSite.longitude != null;
      const hasAddress = selectedJobSite.address != null && selectedJobSite.address.trim() !== '';
      const hasLocationData = hasCoordinates || hasAddress;

      logger.debug('Checking location data availability', {
        hasLocationData,
        hasCoordinates,
        hasAddress,
        latitude: selectedJobSite.latitude,
        longitude: selectedJobSite.longitude,
        address: selectedJobSite.address
      });
      if (hasLocationData) {
        // Only notify that a jobsite timezone will be populated when we actually have location data
        // This prevents user timezone fallback only when we're actually going to populate from jobsite
        onJobSiteSelected?.();

        if (timezonesLoading || !hasTimezonesAvailable) {
          logger.info('Waiting for timezones to load before populating job site timezone', {
            jobSiteId: selectedJobSite.id,
            timezonesLoading,
            hasTimezonesAvailable,
            timezonesCount: timezonesToUse.length
          });
          // Don't update lastJobSiteId yet - will retry when timezones load
          // The effect will re-run when timezonesLoading or hasTimezonesAvailable changes
          return;
        }

        logger.info('Starting timezone population for job site', {
          jobSiteId: selectedJobSite.id,
          jobSiteName: selectedJobSite.jobSiteName,
          timezonesCount: timezonesToUse.length
        });
        // Update lastJobSiteId before calling populateTimeZoneFromJobSite
        lastJobSiteId.current = selectedJobSite.id;
        populateTimeZoneFromJobSite(selectedJobSite);
      } else {
        // No location data - don't prevent user timezone fallback, preserve existing timezone (e.g., from localStorage)
        // Don't call onJobSiteSelected() here so localStorage timezone can remain
        logger.warn('No location data available for job site, preserving existing timezone', {
          jobSiteId: selectedJobSite.id,
          jobSiteName: selectedJobSite.jobSiteName
        });
        // Update lastJobSiteId even when there's no location data
        lastJobSiteId.current = selectedJobSite.id;
        // Don't set an error - just log that we're preserving the existing timezone
        // Clear any previous timezone error since we're preserving the existing value
        setTimeZoneError(null);
      }
    } else {
      logger.debug('Job site selection effect - no action needed', {
        hasSelectedJobSite: !!selectedJobSite,
        jobSiteId: selectedJobSite?.id,
        lastJobSiteId: lastJobSiteId.current,
        idsMatch: selectedJobSite?.id === lastJobSiteId.current
      });

      logger.debug('Job site selection effect - no action needed', {
        hasSelectedJobSite: !!selectedJobSite,
        jobSiteId: selectedJobSite?.id,
        lastJobSiteId: lastJobSiteId.current,
        idsMatch: selectedJobSite?.id === lastJobSiteId.current
      });
    }
  }, [selectedJobSite, timezonesLoading, hasTimezonesAvailable, populateTimeZoneFromJobSite, onJobSiteSelected]);

  return {
    isPopulatingTimeZone,
    timeZoneError,
    clearTimeZoneError
  };
};
